import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Receive conversations" — WeTalk's inbound sign-in details for a trunk, shown ONCE (plan-008
 * ST-UN, design §22.14, DD8-17, B7).
 *
 * The provider must present these when it rings WeTalk. They are WeTalk's, not the provider's: a
 * separate pair, so the only password resting on the SIP edge cannot dial out through the account's
 * provider. `createSipTrunkInboundAuth` answers with the password this one time; the dialog holds it in
 * component state, is unmounted when closed, and only its own button closes it (`dismissible={false}`,
 * the API key's rule, review item 189) so a stray Escape cannot lose it.
 */
import { Button, Dialog, Mono } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import type { SipTrunkInboundAuthView, SipTrunkSettingsView } from "../../api/slice/sip-trunk.js";

type CopyState = "copied" | "refused" | null;

function copyText(text: string, done: (state: CopyState) => void): void {
  const clipboard = globalThis.navigator.clipboard as Clipboard | undefined;
  if (clipboard === undefined) {
    done("refused");
    return;
  }
  clipboard.writeText(text).then(
    () => {
      done("copied");
    },
    () => {
      done("refused");
    },
  );
}

export interface InboundAuthDialogProps {
  readonly trunkName: string;
  readonly issued: SipTrunkInboundAuthView;
  readonly settings: SipTrunkSettingsView | undefined;
  readonly onClose: () => void;
}

export function InboundAuthDialog({ trunkName, issued, settings, onClose }: InboundAuthDialogProps): ReactNode {
  usePortalTranslation("operations");
  const [username, setUsername] = useState<CopyState>(null);
  const [password, setPassword] = useState<CopyState>(null);
  return (
    <Dialog
      open
      title={portalText("operations", "copy_these_sign_in_details_now_1fc8434c")}
      onClose={onClose}
      dismissible={false}
      actions={
        <Button variant="primary" onClick={onClose}>
          {portalText("operations", "i_have_copied_them_730a887d")}</Button>
      }
    >
      <div className="nb-once">
        <p className="nb-once-lead">{portalText("operations", "we_won_t_show_this_password_again_f0558a15")}</p>
        <p className="c-note">
          {portalText("operations", "paste_both_into_98f85b4f")}{trunkName}{portalText("operations", "s_settings_at_your_provider_where_it_asks_how_to_sign_in_w_2df82f8b")}{settings === undefined ? "." : portalText("operations", "with_value_as_the_address_78576cfd", { v0: String(settings.sipEdgeHost) })}
        </p>
        <dl className="nb-facts">
          <dt className="nb-term">{portalText("operations", "username_84c29015")}</dt>
          <dd className="nb-def">
            <Mono wrap>{issued.inboundAuthUsername}</Mono>{" "}
            <Button
              variant="ghost"
              onClick={() => {
                copyText(issued.inboundAuthUsername, setUsername);
              }}
            >
              {username === "copied" ? portalText("operations", "copied_8e3df45a") : portalText("operations", "copy_username_872e643e")}
            </Button>
          </dd>
          <dt className="nb-term">{portalText("operations", "password_8be3c943")}</dt>
          <dd className="nb-def">
            <Mono wrap>{issued.inboundPassword}</Mono>{" "}
            <Button
              variant="ghost"
              onClick={() => {
                copyText(issued.inboundPassword, setPassword);
              }}
            >
              {password === "copied" ? portalText("operations", "copied_8e3df45a") : portalText("operations", "copy_password_cda3abab")}
            </Button>
          </dd>
        </dl>
        <p className="c-note">
          {portalText("operations", "wetalk_keeps_the_password_in_its_vault_and_cannot_show_it_846be745")}</p>
        {username === "refused" || password === "refused" ? (
          <p className="c-note c-note-critical" role="alert">
            {portalText("operations", "this_browser_would_not_copy_it_select_the_text_above_and_c_9a52e037")}</p>
        ) : null}
      </div>
    </Dialog>
  );
}
